import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import { filter } from 'rxjs/operators';
import styled from 'styled-components';
import { LIST_ROW_HEIGHTS } from '../../../constants/list_row_heights';
import { rxMapOfMetricMaps } from '../../../data/providers/rxMetricMap';
import {
  IMetricSettings,
  metricSettings as metricSettingsProvider$,
} from '../../../data/providers/settings/metricSettingsProvider';
import {
  IEnabledMetric,
  IEnabledMetricsMap,
  IMetricMap,
  ITestType,
} from '../../../data/types';
import {
  deselectAllMetrics,
  selectAllMetrics,
  toggleMetric,
} from '../../../data/writers/settings';
import { Row } from '../../layout/Row';
import { ScrollableArea } from '../../layout/ScrollableArea';
import { OpenExpandedMetric } from '../../shared/ExpandedMetric';
import { List } from '../../shared/List/List';
import { ListControlContainer } from '../../shared/List/ListControlContainer';
import {
  ListItem,
  ListItemCell,
  ListItemSafeText,
} from '../../shared/List/ListItem';
import { NoResults } from '../../shared/NoResults';
import { SearchInput } from '../../shared/form/SearchInput';
import { SelectBox } from '../../shared/form/SelectBox';
import { LinearLoading } from '../../shared/loading/LinearLoading';

interface IProps {
  testType: ITestType;
  testTypes: ITestType[];
}
/**
 * @description controls the active metrics for the user
 *
 * @param {IProps} props
 * @returns {React.ReactElement}
 */
export const ActiveMetrics = (props: IProps): React.ReactElement => {
  const [allMetrics, setAllMetrics] = React.useState({});

  const [settingsLoading, setSettingsLoading] = React.useState<boolean>(true);
  const [settings, setSettings] = React.useState<IMetricSettings>(null);
  const [metricSettings, setMetricSettings] =
    React.useState<IEnabledMetricsMap>(null);
  const [selectAll, setSelectAll] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>('');

  React.useEffect((): (() => void) => {
    const listener = rxMapOfMetricMaps
      .pipe(filter((x: IMetricMap): boolean => Object.keys(x).length > 0))
      .subscribe(setAllMetrics);
    return (): void => listener.unsubscribe();
  }, []);

  React.useEffect((): (() => void) => {
    const settingsSubscriber = metricSettingsProvider$.subscribe(
      (mMetrics: IMetricSettings): void => {
        setSettings(mMetrics);
      }
    );
    return (): void => {
      settingsSubscriber.unsubscribe();
    };
  }, []);

  /**
   * @description handles search clear
   *
   * @returns {void}
   */
  const onClearSearch = (): void => setSearch('');

  /**
   * @description checks the current active metrics and toggles selectAll if necessary
   * ex. all metrics were selected and user disables one (set selectAll to false)
   * or user manually enables all the metrics (set selectAll to true) and sets the current metrics
   */
  React.useEffect((): (() => void) => {
    if (!props.testType || !metricSettings) return;
    const currentMetricList = metricSettings[props.testType.id];
    let allEnabled = true;
    if (currentMetricList) {
      allEnabled = currentMetricList.reduce(
        (acc: boolean, curr: IEnabledMetric): boolean => {
          if (!acc) return acc;
          return curr.enabled;
        },
        true
      );
    }
    setSelectAll(allEnabled);
  }, [metricSettings, props.testType]);

  React.useEffect((): void => {
    if (!settings) return;
    setMetricSettings(toEnabledMetrics(settings.metrics));
    setSettingsLoading(false);
  }, [allMetrics, props.testTypes, props.testType, settings]);

  /**
   * @description creates an onToggle handler function for the supplied metric key
   * @param {string} string
   * @returns {Function}
   */
  const getOnToggleMetricHandler = (
    key: string
  ): ((e: React.ChangeEvent<HTMLInputElement>) => void) => {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      const value = !!e.currentTarget.checked;
      toggleMetric(props.testType.id, key, value);
    };
  };

  /**
   * @description converts the current map of disabled metrics to a map of renderable
   * arrays keyed by test type
   * @param {ISettings['metrics']} mMetricSettings the current map of disabled metrics
   * @returns {IEnabledMetricsMap}
   */
  const toEnabledMetrics = (
    mMetricSettings: IMetricSettings['metrics']
  ): IEnabledMetricsMap => {
    if (!mMetricSettings || !props.testTypes) return {};
    return props.testTypes.reduce(
      (acc: IEnabledMetricsMap, testType: ITestType): IEnabledMetricsMap => {
        const mMetrics = allMetrics[testType.id];
        const testSettings = mMetricSettings[testType.id];

        if (!mMetrics) return acc;
        acc[testType.id] = sortBy(
          Object.keys(mMetrics).reduce(
            (a: IEnabledMetric[], metricKey: string): IEnabledMetric[] => {
              if (allMetrics[testType.id][metricKey].isExcludable) {
                a.push({
                  ...allMetrics[testType.id][metricKey],
                  enabled: testSettings
                    ? testSettings[metricKey] !== false
                    : true,
                  key: metricKey,
                });
              }
              return a;
            },
            []
          ),
          (metric: IEnabledMetric): string => metric.label.toLowerCase()
        );
        return acc;
      },
      {}
    );
  };

  /**
   * @description toggles selectAll
   * & delegates the SelectBox click to the appropriate handler
   * @returns {void}
   */
  const toggleSelectAll = (): void => {
    const isSelectAll = !selectAll;
    setSelectAll(isSelectAll);
    if (isSelectAll) {
      onSelectAll();
    } else {
      onDeselectAll();
    }
  };

  /**
   * @description select all the metrics for the current test type
   * @returns {void}
   */
  const onSelectAll = (): void => {
    selectAllMetrics(props.testType.id);
  };
  /**
   * @description deselect all the metrics for the current test type
   * @returns {void}
   */
  const onDeselectAll = (): void => {
    deselectAllMetrics(props.testType.id, allMetrics[props.testType.id]);
  };

  /**
   * @description filters IEnabledMetrics that match the current search
   *
   * @param {IEnabledMetric[]} list the list of metrics to filter
   * @returns {IEnabledMetric[]}
   */
  const applyFilter = (list: IEnabledMetric[]): IEnabledMetric[] => {
    if (!list) return [];
    if (search && search.length > 0) {
      const matcher = new RegExp(search, 'gi');
      return list.filter((metric: IEnabledMetric): boolean =>
        matcher.test(metric.label)
      );
    }
    return list;
  };

  /**
   * @description row rendering function used by react-virtualized
   *
   * @param {number} index the index of the row
   * @param {string} key the key of the row
   * @param {React.CSSProperties} style the style object for the row
   * @returns {React.ReactElement}
   */
  const rowRenderer = ({
    index,
    key,
    style,
  }: {
    index: number;
    key: string;
    style: React.CSSProperties;
  }): React.ReactElement => {
    const metric = metrics[index];
    const metricData = {
      testType: props.testType,
      metric,
    };

    return (
      <ListItem style={style} key={key} role='row'>
        <ListItemCell style={{ flex: 2 }}>
          <ListItemSafeText>
            <OpenExpandedMetric metricData={metricData} />
          </ListItemSafeText>
        </ListItemCell>
        <ListItemCell>
          <SelectBox
            onSelect={getOnToggleMetricHandler(metric.key)}
            selected={metric.enabled}
          />
        </ListItemCell>
      </ListItem>
    );
  };

  const metrics = React.useMemo((): IEnabledMetric[] => {
    return !isEmpty(metricSettings) && !isEmpty(props.testType)
      ? applyFilter(metricSettings[props.testType.id])
      : [];
  }, [JSON.stringify(metricSettings), JSON.stringify(props.testType), search]);

  return (
    <Container>
      <h5>Active Metrics</h5>
      <HeaderRow>
        <HeaderLeft>
          <SearchInput
            value={search}
            onChange={setSearch}
            onClear={onClearSearch}
            style={{ margin: '0px' }}
          />
        </HeaderLeft>
        <HeaderRight>
          <SelectBox
            onSelect={toggleSelectAll}
            selected={selectAll}
            key={0}
            label='Select All'
          />
        </HeaderRight>
      </HeaderRow>
      <ListControlContainer style={{ margin: '0px' }}>
        <ScrollableArea>
          {settingsLoading ? (
            <LinearLoading />
          ) : metrics && metrics.length > 0 ? (
            <List
              listLabel={'active metrics list'}
              listLength={metrics.length}
              rowRenderer={rowRenderer}
              rowHeight={LIST_ROW_HEIGHTS.normal}
            />
          ) : (
            <NoResults message='No Metrics Found' />
          )}
        </ScrollableArea>
      </ListControlContainer>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HeaderRow = styled(Row as any)`
  flex: 0;
  align-items: center;
  padding: 10px 0px;
`;

const HeaderLeft = styled.div`
  flex: 0;
`;
const HeaderRight = styled.div`
  flex: 1;
  padding-right: 27px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;
